import { Component, EventEmitter, Input, NgZone, Optional, Output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SideMenuNode, SideMenuSelectEvent } from '../../../../shared/components/sidemenu/sidemenu.model';
import { AskPanelComponent } from '../../ask-panel/ask-panel.component';
import { FilterRow, FilterControlType, hasDuplicateFilterNames, isFilterRowValid } from '../add-filter/add-filter.component';
import { createPropertyPair } from '../../../../shared/components/property-panel/property-panel.model';
import { GlobalFilterSelection } from '../global-filter-modal/global-filter-modal.component';
import { JsonEditorComponent } from '../../../../shared/elements/json-editor/json-editor.component';
import { ReportsService } from '../../../reports.service';
import { resolveFilterValueColumnKey } from '../select-datasource-modal/select-datasource-modal.model';

interface FilterDatasourceColumn {
  key: string;
  name?: string;
}

interface FilterDatasourceJoin {
  targetDatasetId?: string | null;
  joinType?: string | null;
  columnMappings?: Array<{ leftColumn?: string | null; rightColumn?: string | null }>;
}

interface FilterDatasourceDataset {
  id: string;
  name?: string;
  table_name: string;
  isPrimary?: boolean;
  columns?: FilterDatasourceColumn[];
  joins?: FilterDatasourceJoin[];
}

/**
 * Body of the "Add filter" popup: ASK / Visual Editor / JSON Editor / SQL Query tabs via app-sidemenu.
 * Hosted by InbuiltCustomReportComponent inside its own <app-modal>; the modal footer drives this
 * component's public state/methods (primaryActionLabel/canPerformPrimaryAction/onPrimaryAction), the
 * same pattern used by select-datasource-modal's #dsBody.
 */
@Component({
  selector: 'app-add-filter-modal',
  standalone: false,
  templateUrl: './add-filter-modal.component.html',
  styleUrl: './add-filter-modal.component.scss'
})
export class AddFilterModalComponent {
  @ViewChild('askPanel') private askPanel?: AskPanelComponent;
  @ViewChild('filterJsonEditor') private filterJsonEditor?: JsonEditorComponent;
  @Output() cancelled = new EventEmitter<void>();
  @Output() filterCreated = new EventEmitter<any>();
  // Report's datasource config, forwarded straight down to the Visual Editor's data column picker.
  @Input() selectedDataSource: any = null;
  @Input() reportId: string | number | null = null;
  @Input() editMode = false;

  readonly menuItems: SideMenuNode[] = [
    { id: 'ask', type: 'ask', name: 'ASK' },
    { id: 'visual-editor', type: 'editor', name: 'Visual Editor' },
    { id: 'json-editor', type: 'editor', name: 'JSON Editor' },
    { id: 'sql-query', type: 'query', name: 'SQL Query' }
  ];
  selectedMenuId = 'ask';

  askPromptValue = '';
  askNotes: string[] = [];
  askFeedbackMessage = '';
  // Visual Editor / JSON Editor / SQL Query all start empty until the user fills them in (or ASK generates a draft).
  filterRows: FilterRow[] = [];
  globalFilterSelections: GlobalFilterSelection[] = [];
  tableFilterSelections: GlobalFilterSelection[] = [];
  jsonEditorInitialJson: any = {};
  currentSqlQueryCode = '';
  sqlQueryCode = '';
  isSaving = false;
  isValidatingVisual = false;
  visualEditorValidated = false;
  private originalFilterRowsSnapshot = '';
  private originalGlobalFilterSnapshot = '';
  private originalTableFilterSnapshot = '';
  private readonly originalFieldsByRowId = new Map<string, any>();
  private jsonEditorSourceJson: any = {};
  private visualValidationRequestId = 0;

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    @Optional() private ngZone?: NgZone
  ) {}

  onMenuSelected(event: SideMenuSelectEvent): void {
    if (event?.node?.id) {
      this.selectedMenuId = event.node.id;
      if (this.selectedMenuId === 'json-editor') { this.syncVisualEditorToJson(this.editMode); }
    }
  }

  // app-modal keeps this component mounted between opens (see select-datasource-modal-pattern memory) -
  // the host must call this before showing the modal again so a previous draft isn't still here.
  resetState(): void {
    this.selectedMenuId = 'ask';
    this.askPromptValue = '';
    this.askPanel?.resetState();
    this.askNotes = [];
    this.askFeedbackMessage = '';
    this.filterRows = [];
    this.globalFilterSelections = [];
    this.tableFilterSelections = [];
    this.jsonEditorInitialJson = {};
    this.jsonEditorSourceJson = {};
    this.currentSqlQueryCode = '';
    this.sqlQueryCode = '';
    this.isSaving = false;
    this.invalidateVisualValidation();
    this.editMode = false;
    this.originalFilterRowsSnapshot = '';
    this.originalGlobalFilterSnapshot = '';
    this.originalTableFilterSnapshot = '';
    this.originalFieldsByRowId.clear();
  }

  initializeForEdit(elementJson: any, constructedQuery: any): void {
    this.resetState();
    this.editMode = true;
    this.setApiResult(elementJson, constructedQuery);
    this.currentSqlQueryCode = this.sqlQueryCode;
    this.sqlQueryCode = '';
    this.selectedMenuId = 'visual-editor';

    const fields = Array.isArray(this.jsonEditorInitialJson?.fields)
      ? this.jsonEditorInitialJson.fields
      : [];
    this.filterRows = fields.map((field: any, index: number) => {
      const row = this.createFilterRow(field, index);
      this.originalFieldsByRowId.set(row.id, field);
      return row;
    });
    this.originalFilterRowsSnapshot = this.getFilterRowsSnapshot();
    this.originalGlobalFilterSnapshot = this.getGlobalFilterSnapshot();
    this.originalTableFilterSnapshot = this.getTableFilterSnapshot();
  }

  onFilterDraftChange(rows: FilterRow[]): void {
    this.filterRows = rows;
    if (this.selectedMenuId === 'visual-editor') {
      this.invalidateVisualValidation();
      this.sqlQueryCode = '';
      if (!this.editMode) { this.syncVisualEditorToJson(true); }
    }
  }

  onGlobalFilterSelectionsChange(selections: GlobalFilterSelection[]): void {
    this.globalFilterSelections = selections.map(selection => ({
      ...selection,
      selectedValues: [...selection.selectedValues]
    }));
    if (this.selectedMenuId === 'visual-editor') {
      this.invalidateVisualValidation();
      this.sqlQueryCode = '';
      if (!this.editMode) { this.syncVisualEditorToJson(true); }
    }
  }

  onTableFilterSelectionsChange(selections: GlobalFilterSelection[]): void {
    this.tableFilterSelections = selections.map(selection => ({
      ...selection,
      selectedValues: [...selection.selectedValues]
    }));
    if (this.selectedMenuId === 'visual-editor') {
      this.invalidateVisualValidation();
      this.sqlQueryCode = '';
      if (!this.editMode) { this.syncVisualEditorToJson(true); }
    }
  }

  onJsonTextChange(_value: any): void {
    if (this.filterJsonEditor?.isChanged) {
      this.sqlQueryCode = '';
      this.invalidateVisualValidation();
    }
  }

  onAskGenerated(response: any): void {
    this.askFeedbackMessage = '';
    this.invalidateVisualValidation();
    const elementJson = this.parseElementJson(response?.elementJson);
    const fields = elementJson?.fields;

    if (String(response?.elementType || '').toLowerCase() !== 'filter' || !Array.isArray(fields) || fields.length === 0) {
      this.askFeedbackMessage = 'Search result are not relevant.Try a different search';
      return;
    }
    const notes = response?.requirements?.notes;
    this.askNotes = Array.isArray(notes)
      ? notes.filter((note: any): note is string => typeof note === 'string' && !!note.trim())
      : typeof notes === 'string' && notes.trim() ? [notes] : [];
    this.jsonEditorInitialJson = elementJson;
    this.jsonEditorSourceJson = elementJson;
    this.originalFieldsByRowId.clear();
    this.filterRows = fields.map((field: any, index: number) => {
      const row = this.createFilterRow(field, index);
      this.originalFieldsByRowId.set(row.id, field);
      return row;
    });
    this.sqlQueryCode = typeof response?.generatedSql === 'string'
      ? response.generatedSql
      : response?.generatedSql == null ? '' : String(response.generatedSql);
  }

  onJsonValidated(jsonText: string): void {
    const elementJson = this.parseElementJson(jsonText);
    if (!Array.isArray(elementJson?.fields)) { return; }

    this.invalidateVisualValidation();
    this.visualEditorValidated = true;
    this.jsonEditorSourceJson = elementJson;
    this.originalFieldsByRowId.clear();
    this.filterRows = elementJson.fields.map((field: any, index: number) => {
      const row = this.createFilterRow(field, index);
      this.originalFieldsByRowId.set(row.id, field);
      return row;
    });
  }

  get primaryActionLabel(): string {
    if (this.selectedMenuId === 'ask') { return 'Generate'; }
    return this.editMode ? 'Update filter' : 'Add filter';
  }

  get showPrimaryAction(): boolean {
    if (this.selectedMenuId === 'visual-editor') {
      return this.editMode ? this.hasVisualChanges : this.visualEditorValidated;
    }
    if (this.selectedMenuId === 'json-editor') {
      return this.filterJsonEditor?.isValidated === true &&
        (!this.editMode || this.filterJsonEditor?.isChanged === true);
    }
    if (!this.editMode) { return true; }
    if (this.selectedMenuId === 'ask') { return !!this.askPromptValue.trim(); }
    return false;
  }

  get hasVisualChanges(): boolean {
    return this.getFilterRowsSnapshot() !== this.originalFilterRowsSnapshot ||
      this.getGlobalFilterSnapshot() !== this.originalGlobalFilterSnapshot ||
      this.getTableFilterSnapshot() !== this.originalTableFilterSnapshot;
  }

  get canPerformPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') {
      return this.askPanel?.hasReportId === true && !!this.askPromptValue.trim() && !this.askPanel?.generating;
    }
    if (this.selectedMenuId === 'visual-editor') {
      return !this.isSaving && this.visualEditorValidated && this.filterRows.length > 0 &&
        this.filterRows.every(isFilterRowValid) && !hasDuplicateFilterNames(this.filterRows) &&
        (!this.editMode || this.hasVisualChanges);
    }
    if (this.selectedMenuId === 'json-editor') {
      const elementJson = this.parseElementJson(this.getCurrentJsonEditorValue());
      return !this.isSaving && this.filterJsonEditor?.isValidated === true &&
        Array.isArray(elementJson?.fields) && elementJson.fields.length > 0 &&
        (!this.editMode || this.filterJsonEditor?.isChanged === true);
    }
    return false;
  }

  get canValidateVisual(): boolean {
    return this.selectedMenuId === 'visual-editor' && !this.isSaving && !this.isValidatingVisual && !this.visualEditorValidated &&
      this.filterRows.length > 0 && this.filterRows.every(isFilterRowValid) &&
      !hasDuplicateFilterNames(this.filterRows);
  }

  get canValidateJson(): boolean {
    return this.selectedMenuId === 'json-editor' && !this.isSaving &&
      this.filterJsonEditor?.isValidated !== true &&
      this.hasNonEmptyJson(this.getCurrentJsonEditorValue());
  }

  onPrimaryAction(): void {
    if (this.selectedMenuId === 'ask') {
      this.askPanel?.generateReport();
      return;
    }
    if (!this.canPerformPrimaryAction) { return; }
    const generatedJson = this.selectedMenuId === 'json-editor'
      ? this.parseElementJson(this.getCurrentJsonEditorValue()) || {}
      : this.createVisualEditorJson();
    this.filterCreated.emit({
      filters: this.filterRows,
      elementJson: generatedJson,
      selectedMenuId: this.selectedMenuId,
      isEditMode: this.editMode
    });
  }

  onValidateJson(): void {
    if (!this.canValidateJson) { return; }
    this.filterJsonEditor?.validateJson();
  }

  onValidateVisualJson(): void {
    if (!this.canValidateVisual) { return; }
    const elementJson = this.createVisualEditorJson();
    this.jsonEditorInitialJson = elementJson;
    this.jsonEditorSourceJson = elementJson;
    const submittedSnapshot = this.getVisualValidationSnapshot();
    const requestId = ++this.visualValidationRequestId;
    this.isValidatingVisual = true;

    this.reportsService.validateReportJSON({
      element_json: JSON.stringify(elementJson),
      selectedFilters: {}
    }).subscribe({
      next: (response: any) => {
        if (this.ngZone) {
          this.ngZone.run(() => this.handleVisualValidationSuccess(requestId, submittedSnapshot, response));
        } else {
          this.handleVisualValidationSuccess(requestId, submittedSnapshot, response);
        }
      },
      error: (error: any) => {
        if (this.ngZone) {
          this.ngZone.run(() => this.handleVisualValidationError(requestId, error));
        } else {
          this.handleVisualValidationError(requestId, error);
        }
      }
    });
  }

  private handleVisualValidationSuccess(requestId: number, submittedSnapshot: string, response: any): void {
    if (requestId !== this.visualValidationRequestId) { return; }
    this.isValidatingVisual = false;
    if (submittedSnapshot !== this.getVisualValidationSnapshot()) { return; }
    if (!response?.success) {
      this.toastr.error(this.getValidationErrorMessage(response?.error), '', {
        timeOut: 5000,
        positionClass: 'toast-top-center'
      });
      return;
    }

    this.visualEditorValidated = true;
    this.sqlQueryCode = this.toQueryText(response?.constructedQuery);
    this.toastr.success('JSON Validated successfully', '', {
      timeOut: 5000,
      positionClass: 'toast-top-center'
    });
  }

  private handleVisualValidationError(requestId: number, error: any): void {
    if (requestId !== this.visualValidationRequestId) { return; }
    this.isValidatingVisual = false;
    this.visualEditorValidated = false;
    this.toastr.error(this.getValidationErrorMessage(error?.error?.error ?? error?.error ?? error?.message), '', {
      timeOut: 5000,
      positionClass: 'toast-top-center'
    });
  }

  onConstructedQueryChange(query: any): void {
    const queryText = typeof query === 'string'
      ? query
      : query == null ? '' : String(query);
    if (queryText) {
      this.sqlQueryCode = queryText;
    } else if (this.filterJsonEditor?.isChanged) {
      this.sqlQueryCode = '';
    }
  }

  private syncVisualEditorToJson(force = false): void {
    if ((!force && this.filterJsonEditor?.isChanged === true) || this.filterRows.length === 0 ||
      !this.filterRows.every(isFilterRowValid) || hasDuplicateFilterNames(this.filterRows)) {
      return;
    }

    const elementJson = this.createVisualEditorJson();
    this.jsonEditorInitialJson = elementJson;
    this.jsonEditorSourceJson = elementJson;
  }

  private createVisualEditorJson(): any {
    const generatedJson = this.createElementJsonFromRows();
    return this.editMode || this.originalFieldsByRowId.size > 0
      ? this.createEditedElementJson(generatedJson)
      : generatedJson;
  }

  private invalidateVisualValidation(): void {
    this.visualValidationRequestId++;
    this.isValidatingVisual = false;
    this.visualEditorValidated = false;
  }

  private getVisualValidationSnapshot(): string {
    return `${this.getFilterRowsSnapshot()}|${this.getGlobalFilterSnapshot()}|${this.getTableFilterSnapshot()}`;
  }

  private getValidationErrorMessage(error: any): string {
    const messages = Array.isArray(error)
      ? error.map(String).filter(message => !!message.trim())
      : error == null ? [] : [String(error)];
    return messages.length ? messages.join('\n') : 'Unable to validate filter JSON.';
  }

  private toQueryText(query: any): string {
    if (typeof query === 'string') { return query; }
    if (query == null) { return ''; }
    if (typeof query !== 'object') { return String(query); }
    try {
      return JSON.stringify(query, null, 2);
    } catch {
      return String(query);
    }
  }

  setSaving(isSaving: boolean): void {
    this.isSaving = isSaving;
  }

  private getCurrentJsonEditorValue(): any {
    return this.filterJsonEditor?.getJSON() ?? this.jsonEditorInitialJson;
  }

  private hasNonEmptyJson(value: any): boolean {
    if (value == null) { return false; }
    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) { return false; }
      try {
        value = JSON.parse(text);
      } catch {
        return true;
      }
    }
    if (!value || typeof value !== 'object') { return false; }
    return Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0;
  }

  setApiResult(elementJson: any, constructedQuery: any): void {
    this.currentSqlQueryCode = '';
    const parsedElementJson = this.parseElementJson(elementJson);
    if (parsedElementJson?.query_filter && typeof parsedElementJson.query_filter === 'object') {
      const queryFilter = { ...parsedElementJson.query_filter };
      delete queryFilter.applied_filter;
      this.jsonEditorInitialJson = {
        ...parsedElementJson,
        query_filter: this.qualifyDboTableKeys(queryFilter)
      };
    } else {
      this.jsonEditorInitialJson = parsedElementJson ?? {};
    }
    this.jsonEditorSourceJson = this.jsonEditorInitialJson;
    this.sqlQueryCode = typeof constructedQuery === 'string'
      ? constructedQuery
      : constructedQuery == null ? '' : String(constructedQuery);
  }

  private createEditedElementJson(generatedJson: any): any {
    const originalJson = this.jsonEditorSourceJson && typeof this.jsonEditorSourceJson === 'object'
      ? this.jsonEditorSourceJson
      : {};
    const generatedFields = Array.isArray(generatedJson?.fields) ? generatedJson.fields : [];
    const fields = this.filterRows.map((row, index) => ({
      ...(this.originalFieldsByRowId.get(row.id) || {}),
      ...(generatedFields[index] || {})
    })).map((field, index) => {
      const row = this.filterRows[index];
      const originalField = this.originalFieldsByRowId.get(row.id);
      if (originalField && this.normalizeFilterType(originalField.type) === generatedFields[index]?.type) {
        field.type = originalField.type;
        if (row.name === originalField.label && typeof originalField.name === 'string') {
          field.name = originalField.name;
        }
      }
      return field;
    });
    const hasGlobalFilterChanges = this.getGlobalFilterSnapshot() !== this.originalGlobalFilterSnapshot ||
      this.getTableFilterSnapshot() !== this.originalTableFilterSnapshot;

    const queryFilter = hasGlobalFilterChanges
      ? generatedJson?.query_filter
      : originalJson?.query_filter ?? generatedJson?.query_filter;

    return {
      ...originalJson,
      ...generatedJson,
      fields,
      query_filter: this.qualifyDboTableKeys(queryFilter)
    };
  }

  private getFilterRowsSnapshot(): string {
    return JSON.stringify(this.filterRows.map(row => ({
      name: row.name,
      type: row.type,
      columns: row.columns.map(({ label, value }) => ({ label, value })),
      properties: row.properties
    })));
  }

  private getGlobalFilterSnapshot(): string {
    return JSON.stringify(this.globalFilterSelections);
  }

  private getTableFilterSnapshot(): string {
    return JSON.stringify(this.tableFilterSelections);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  private parseElementJson(value: any): any {
    if (typeof value !== 'string') { return value; }
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private createFilterRow(field: any, index: number): FilterRow {
    const type = this.normalizeFilterType(field?.type);
    const mapping = type === 'hierarchal' || type === 'hierarchal-delimiter'
      ? field?.column ?? field?.name
      : field?.name;
    const entries = this.getColumnMappings(mapping);
    const columns = entries.map(([labelColumn, valueColumn]) => {
      const label = this.resolveDataColumn(labelColumn) || this.resolveDataColumn(valueColumn);
      const resolvedValue = this.resolveDataColumn(valueColumn);
      // The AI response only gives a genuinely distinct value column sometimes - when it doesn't
      // (resolves empty or to the same column as the label), derive it the same way the Visual Editor
      // does: via the label column's filterColumn mapping, falling back to the label itself.
      const value = resolvedValue && resolvedValue !== label
        ? resolvedValue
        : (label ? resolveFilterValueColumnKey(label, this.selectedDataSource) : resolvedValue);
      return { ...createPropertyPair(), label, value };
    }).filter(pair => !!pair.label && !!pair.value);
    const selectedColumns = type === 'hierarchal' ? columns : columns.slice(0, 1);
    const rawDefaultSelection = Array.isArray(field?.default_selection)
      ? field.default_selection.map(String)
      : field?.default_selection != null ? [String(field.default_selection)] : ['All'];
    const defaultSelection = rawDefaultSelection.map(value => {
      const normalized = value.toLowerCase();
      return normalized === 'all' ? 'All' : normalized === 'none' ? 'None' : value;
    });
    const width = Number(field?.class);

    return {
      id: `ask-filter-${Date.now()}-${index}`,
      name: String(field?.label || (typeof field?.name === 'string' ? field.name : '') || ''),
      type,
      columns: selectedColumns.length ? selectedColumns : [createPropertyPair()],
      properties: {
        defaultSelection,
        visibility: field?.visibility !== false,
        searchType: String(field?.search || '').toLowerCase() === 'equal' ? 'Equal' : 'Like',
        width: Number.isFinite(width) ? Math.max(3, Math.min(12, width)) : 3
      }
    };
  }

  private normalizeFilterType(value: any): FilterControlType | '' {
    const type = String(value || '').trim().toLowerCase().replace(/[_\s]/g, '-');
    if (type === 'hierarchical') { return 'hierarchal'; }
    if (type === 'hierarchical-delimiter' || type === 'hierarchicaldelimiter') { return 'hierarchal-delimiter'; }
    if (type === 'hierarchaldelimiter') { return 'hierarchal-delimiter'; }
    if (type === 'singleselect') { return 'single-select'; }
    if (type === 'hierarchal' || type === 'hierarchal-delimiter' || type === 'multiselect' || type === 'single-select') {
      return type;
    }
    return '';
  }

  private getColumnMappings(mapping: any): Array<[string, string]> {
    if (typeof mapping === 'string' && mapping.trim()) { return [[mapping, mapping]]; }
    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) { return []; }
    return Object.entries(mapping)
      .filter(([label, value]) => !!label && value != null && String(value).trim() !== '')
      .map(([label, value]) => [label, String(value)]);
  }

  private resolveDataColumn(candidate: string): string | null {
    const normalized = candidate.trim().toLowerCase();
    if (!normalized) { return null; }
    let datasource: any = this.selectedDataSource;
    if (typeof datasource === 'string') {
      try {
        datasource = JSON.parse(datasource);
      } catch {
        return null;
      }
    }
    const datasets: any[] = datasource?.datasets || [];
    for (const dataset of datasets) {
      for (const column of dataset?.columns || []) {
        const key = String(column.key || '');
        const selectionKey = `${dataset.id}.${key}`;
        const candidates = [selectionKey, key, column.alias, column.name]
          .map(value => String(value || '').trim().toLowerCase());
        if (candidates.includes(normalized)) { return selectionKey; }
      }
    }
    return null;
  }

  private createElementJsonFromRows(): { fields: any[]; query_filter: any } {
    const fields = this.filterRows.map((row, index) => {
      const columnMappings = Object.fromEntries(row.columns
        .filter(pair => !!pair.label?.trim() && !!pair.value?.trim())
        .map(pair => [
          this.getSourceColumnName(pair.label!),
          this.getSourceColumnName(pair.value!)
        ]));
      const field: any = {
        label: row.name.trim(),
        type: row.type,
        default_selection: row.properties.defaultSelection.length === 1
          ? row.properties.defaultSelection[0]
          : row.properties.defaultSelection,
        visibility: row.properties.visibility,
        class: row.properties.width,
        search: row.properties.searchType.toLowerCase()
      };
      if (row.type === 'hierarchal') {
        field.name = this.toFilterKey(row.name, index);
        field.column = columnMappings;
      } else {
        field.name = columnMappings;
      }
      return field;
    });

    return { fields, query_filter: this.createQueryFilter() };
  }

  private createQueryFilter(): Record<string, any> {
    const datasets = this.getDatasourceDatasets();
    const primary = datasets.find(dataset => dataset.isPrimary) || datasets[0];
    const orderedDatasets = primary
      ? [primary, ...datasets.filter(dataset => dataset.id !== primary.id)]
      : [];
    const aliasesByDatasetId = new Map<string, { alias: string; section: string; index: number }>();
    const tablekey: Record<string, string> = {};
    const columnList: Record<string, string[]> = {};
    const whereList: Record<string, string[]> = {};
    const columnkey: Record<string, string> = {};

    orderedDatasets.forEach((dataset, index) => {
      const alias = this.getSqlTableAlias(index);
      const section = index === 0 ? 'main' : `source${index + 1}`;
      aliasesByDatasetId.set(dataset.id, { alias, section, index });
      tablekey[section] = `${this.qualifyDboTableName(dataset.table_name)} ${alias}`;
      columnList[section] = [];
      whereList[section] = [];
    });

    const addColumn = (dataset: FilterDatasourceDataset | undefined, columnKey: string | null | undefined): string | null => {
      if (!dataset || !columnKey) { return null; }
      const location = aliasesByDatasetId.get(dataset.id);
      const column = dataset.columns?.find(candidate => candidate.key === columnKey);
      if (!location || !column) { return null; }
      const reference = `${location.alias}.${this.quoteSqlIdentifier(column.key)}`;
      if (!columnList[location.section].includes(reference)) {
        columnList[location.section].push(reference);
      }
      return reference;
    };

    const findDatasetColumn = (selectionKey: string): { dataset: FilterDatasourceDataset; column: FilterDatasourceColumn } | null => {
      for (const dataset of orderedDatasets) {
        const prefix = `${dataset.id}.`;
        if (!selectionKey.startsWith(prefix)) { continue; }
        const key = selectionKey.slice(prefix.length);
        const column = dataset.columns?.find(candidate => candidate.key === key);
        if (column) { return { dataset, column }; }
      }
      return null;
    };

    this.filterRows.forEach(row => row.columns.forEach(pair => {
      [pair.label, pair.value].forEach(selectionKey => {
        if (!selectionKey) { return; }
        const selected = findDatasetColumn(selectionKey);
        const reference = selected ? addColumn(selected.dataset, selected.column.key) : null;
        if (selected && reference) {
          columnkey[reference] = this.quoteSqlIdentifier(selected.column.name || selected.column.key);
        }
      });
    }));

    const baseWhereConditions: string[] = [];
    this.globalFilterSelections.forEach(condition => {
      const selected = findDatasetColumn(condition.columnKey);
      const dataset = selected?.dataset || orderedDatasets.find(item => item.table_name === condition.tableName);
      const columnKey = selected?.column.key || condition.columnName;
      const reference = addColumn(dataset, columnKey);
      if (!reference || !condition.selectedValues.length) { return; }
      const values = this.quoteSqlValues(condition.selectedValues);
      baseWhereConditions.push(`${reference} IN (${values})`);
    });

    // Table filters go into the whereList of the section (CTE) of the table the column belongs to.
    this.tableFilterSelections.forEach(condition => {
      const selected = findDatasetColumn(condition.columnKey);
      const location = selected ? aliasesByDatasetId.get(selected.dataset.id) : undefined;
      if (!selected || !location || !condition.selectedValues.length) { return; }
      const reference = `${location.alias}.${this.quoteSqlIdentifier(selected.column.key)}`;
      whereList[location.section].push(`${reference} IN (${this.quoteSqlValues(condition.selectedValues)})`);
    });

    const constraints: string[] = [];
    orderedDatasets.slice(1).forEach(dataset => {
      const source = aliasesByDatasetId.get(dataset.id);
      (dataset.joins || []).forEach(join => {
        const targetDataset = orderedDatasets.find(candidate => candidate.id === join.targetDatasetId);
        const target = targetDataset ? aliasesByDatasetId.get(targetDataset.id) : undefined;
        if (!source || !target || !join.columnMappings?.length) { return; }
        const joinConditions = join.columnMappings.flatMap(mapping => {
          if (!mapping.leftColumn || !mapping.rightColumn) { return []; }
          addColumn(targetDataset, mapping.leftColumn);
          addColumn(dataset, mapping.rightColumn);
          return [
            `${target.alias}.${this.quoteSqlIdentifier(mapping.leftColumn)} = ` +
            `${source.alias}.${this.quoteSqlIdentifier(mapping.rightColumn)}`
          ];
        });
        if (joinConditions.length) {
          constraints.push(
            `${join.joinType || 'LEFT JOIN'} CTE${source.index + 1} ${source.alias} ON ${joinConditions.join(' AND ')}`
          );
        }
      });
    });

    return {
      declarekey: '',
      tablekey,
      columnList,
      whereList,
      constraints,
      columnkey,
      baseWhere: baseWhereConditions.join(' AND '),
      totalCount: '',
      defaultQuery: ''
    };
  }

  private getDatasourceDatasets(): FilterDatasourceDataset[] {
    let datasource = this.selectedDataSource;
    if (typeof datasource === 'string') {
      datasource = this.parseElementJson(datasource);
    }
    return Array.isArray(datasource?.datasets)
      ? datasource.datasets.filter((dataset: any) =>
        typeof dataset?.id === 'string' && typeof dataset?.table_name === 'string'
      )
      : [];
  }

  private quoteSqlValues(values: string[]): string {
    return values.map(value => `'${String(value).replace(/'/g, "''")}'`).join(', ');
  }

  private getSqlTableAlias(index: number): string {
    let alias = '';
    let currentIndex = index;
    do {
      alias = String.fromCharCode(65 + (currentIndex % 26)) + alias;
      currentIndex = Math.floor(currentIndex / 26) - 1;
    } while (currentIndex >= 0);
    return alias;
  }

  private quoteSqlIdentifier(identifier: string): string {
    const unwrapped = identifier.startsWith('[') && identifier.endsWith(']')
      ? identifier.slice(1, -1)
      : identifier;
    return `[${unwrapped.replace(/\]/g, ']]')}]`;
  }

  private getSourceColumnName(selectionKey: string): string {
    let datasource: any = this.selectedDataSource;
    if (typeof datasource === 'string') {
      datasource = this.parseElementJson(datasource);
    }
    for (const dataset of datasource?.datasets || []) {
      for (const column of dataset?.columns || []) {
        if (`${dataset.id}.${column.key}` === selectionKey) {
          return String(column.key);
        }
      }
    }
    return selectionKey;
  }

  private qualifyDboTableName(tableName: string): string {
    const trimmedTableName = tableName.trim();
    if (/(?:^|\.)\s*\[?dbo\]?\s*\./i.test(trimmedTableName)) {
      return trimmedTableName;
    }

    const quotedTableName = trimmedTableName.startsWith('[') && trimmedTableName.endsWith(']')
      ? trimmedTableName
      : `[${trimmedTableName}]`;
    return `[dbo].${quotedTableName}`;
  }

  private qualifyDboTableKeys(queryFilter: any): any {
    if (!queryFilter || typeof queryFilter !== 'object' || Array.isArray(queryFilter)) {
      return queryFilter;
    }

    const tablekey = queryFilter.tablekey;
    if (typeof tablekey === 'string') {
      return { ...queryFilter, tablekey: this.qualifyDboTableKey(tablekey) };
    }
    if (!tablekey || typeof tablekey !== 'object' || Array.isArray(tablekey)) {
      return queryFilter;
    }

    return {
      ...queryFilter,
      tablekey: Object.fromEntries(Object.entries(tablekey).map(([key, value]) => [
        key,
        typeof value === 'string' ? this.qualifyDboTableKey(value) : value
      ]))
    };
  }

  private qualifyDboTableKey(tableKey: string): string {
    const trimmedTableKey = tableKey.trim();
    if (!trimmedTableKey) { return tableKey; }
    const tableAndAlias = trimmedTableKey.match(/^(.*\S)\s+([A-Za-z_][A-Za-z0-9_]*)$/);
    if (!tableAndAlias) {
      return this.qualifyDboTableName(trimmedTableKey);
    }
    return `${this.qualifyDboTableName(tableAndAlias[1])} ${tableAndAlias[2]}`;
  }

  private toFilterKey(label: string, index: number): string {
    const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return key || `filter_${index + 1}`;
  }
}
